import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { useParallaxScene } from '../../application/motion/useParallaxScene';
import { shareApp } from '../../application/pwa/shareApp';
import { ROUTE_PATHS } from '../../app/routes';
import { LavenderMark, ChevronDownIcon, ShareIcon } from '../chrome/BrandIcons';
import { ASSET_ROOT, FOREGROUND_LAYERS, SCENE_LAYERS, SceneLayer } from './LandingSceneLayers';

/**
 * The landing screen (FR-02): the proposition, one primary action, and the
 * garden doorway behind both (SPEC-021) — a paved path entering at the
 * visitor's feet, beds passing on one shared stride, wildlife taking turns on
 * a stage, all under drifting sun and shade.
 *
 * What the scene is MADE of lives in LandingSceneLayers.js; this file is the
 * page: which planes are drawn on which side of the hero, and the arrival.
 */


/**
 * The doorway is walked through ONCE per visit. The veil, the settle and the
 * content rise replay on a fresh arrival, not on every wander back to the
 * front door from inside the app — module scope, like installPrompt.js, is
 * exactly the lifetime "this SPA session" has.
 */
let hasArrivedThisSession = false;

export default function LandingScreen() {
    const { localeCode, languageCode, t } = useLocale();
    const sceneRef = useParallaxScene();
    const [isArriving] = useState(() => {
        const firstVisit = !hasArrivedThisSession;
        hasArrivedThisSession = true;

        return firstVisit;
    });

    /* The proposition is stored whole — the share card, the plant sheet and the
       app-share text all need the sentence intact — and `brand.proposition.accent`
       names the tail this language wants in the second ink. A catalogue whose
       accent is not a suffix of its proposition falls back to one ink, rather
       than rendering the tail twice or dropping it. */
    const proposition = t('brand.proposition');
    const accent = t('brand.proposition.accent');
    const isAccented = accent.length > 0
        && accent.length < proposition.length
        && proposition.endsWith(accent);
    const lead = isAccented ? proposition.slice(0, -accent.length) : proposition;
    const [shareResult, setShareResult] = useState(null);

    // The scenic card first, the address underneath it: a picture is what
    // travels where people actually pass an app on, and every way the picture
    // can fail lands on the link (SPEC-020 D-5). The address itself is the
    // locale's canonical root rather than the current href, so a Firebase mirror
    // never hands out its own hostname as the product's (D-6).
    const share = async () => {
        setShareResult(await shareApp({
            localeCode,
            languageCode,
            title: t('home.share.title'),
            text: t('home.intro'),
        }));
    };

    return (
        /* The parallax ref sits on the SECTION, not on `.landing-scene`, because
           the scene is now two containers straddling the hero and both read the
           same three custom properties. Their nearest common ancestor is the
           only place one style write per frame can still drive all of them.
           `.landing` has no z-index of its own and gains none from this, so the
           fixed children still escape to the root stacking context. */
        <section className={isArriving ? 'landing landing--arriving' : 'landing'} ref={sceneRef}>
            <div className="landing-scene" aria-hidden="true">
                {/* The world wrapper exists so the arrival settle and the
                    standing sway have an element of their own: the layers
                    already spend their transform on parallax, and the art
                    inside them spends its own on the walk (landingScene.css's
                    two-system rule, extended by one). */}
                <div className="landing-scene__world">
                    {SCENE_LAYERS.map((layer) => <SceneLayer key={layer.key} layer={layer} />)}
                </div>

                {/* The sun system (SPEC-021 D-5): beams, drifting shade and
                    falling motes, alpha-composited between the world and the
                    scrim. Painterly, not physical — but always from the sun's
                    side of the sky, and never via blend modes or filters,
                    both of which have stopped iOS compositing this exact
                    fixed, transformed scene before. */}
                <div className="landing-scene__sunbeams" />
                <div className="landing-scene__shade" />
                <div className="landing-scene__motes" />

                <div className="landing-scene__scrim" />

                {/* The door itself: canvas-coloured, continuing the boot
                    screen's cream, lifting once on arrival (D-6). Its BASE
                    state is invisible, so reduced motion can never leave the
                    garden covered. */}
                <div className="landing-scene__veil" />
            </div>

            <div className="landing__hero">
                {/* Two inks, the same pair the wordmark uses: the proposition in
                    forest with its payload word in --brand-pink, so the front door
                    and the lockup are recognisably one brand speaking.

                    The split is DATA, not a rule about words. The emphasis does
                    not fall on the same part of speech in all three languages, and
                    a component that guessed by counting words would be wrong in the
                    first language that reordered the sentence. */}
                <h1 className="landing__headline">
                    {lead}
                    {isAccented ? <span className="landing__headline-accent">{accent}</span> : null}
                </h1>

                {/* The brand mark, standing on the rule under the headline —
                    the one place §6.3 asks for the lavender at size. The header
                    above gives it up (`brandMark: false`) so the front door says
                    it once rather than twice.

                    It was removed in PR-13.6 and reinstated here; the divider
                    geometry is the mark's, not a bare rule's, which is why the
                    11rem width and the rules' bottom margin came back with it. */}
                <div className="landing__divider" aria-hidden="true">
                    <span className="landing__divider-rule" />
                    <LavenderMark className="landing__divider-mark" sizes="32px" />
                    <span className="landing__divider-rule" />
                </div>

                <div className="landing__actions">

                    <Link
                        className="button button--primary landing__cta"
                        to={`/${localeCode}${ROUTE_PATHS.plants}`}
                    >
                        {t('home.cta.discover')}
                        {/* Rendered here, not as a scene layer, so it walks in FRONT
                            of the action rather than behind the whole composition. */}
                        <img
                            className="landing__ladybird"
                            src={`${ASSET_ROOT}/layers/ladybird-70.webp`}
                            srcSet={`${ASSET_ROOT}/layers/ladybird-70.webp 70w, ${ASSET_ROOT}/layers/ladybird-140.webp 140w`}
                            sizes="30px"
                            alt=""
                            loading="eager"
                            decoding="async"
                            draggable="false"
                            aria-hidden="true"
                        />

                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </Link>

                    {/* A quiet SECOND action. The landing screen's job is one
                        primary action (FR-02), and a share control drawn to
                        compete with `Discover your plants` would be a worse
                        front door than no share control at all — so this is a
                        paper plate beside the CTA's saturated forest, at the
                        44px minimum target (SPEC-020 D-8). */}
                    <button type="button" className="landing__share" onClick={share}>
                        <ShareIcon size={18} />
                        {t('home.share.action')}
                    </button>

                    {/* The operating system's sheet says its own piece, so only
                        the clipboard — the outcome with no chrome of its own —
                        prints anything. A live region, so it is announced rather
                        than merely drawn, and always rendered so the row does
                        not change height when it fills. */}
                    <p className="landing__share-status" role="status">
                        {shareResult === 'copied' ? t('home.share.copied') : ''}
                    </p>
                </div>

                <div className="landing__scroll-hint" aria-hidden="true">
                    <ChevronDownIcon />
                </div>
            </div>

            {/* The front plane, in front of the hero. Same `.landing-scene`
                class as the container above it — that is what hands it the
                fixed geometry, the stride and the reduced-motion rule, all of
                which are written against that class — with the modifier
                dropping the sky, which a foreground has no business painting.

                It carries its OWN veil: the door has to cover the whole
                garden, and the veil in the container behind cannot reach a
                layer painting above it. */}
            <div className="landing-scene landing-scene--foreground" aria-hidden="true">
                <div className="landing-scene__world">
                    {FOREGROUND_LAYERS.map((layer) => <SceneLayer key={layer.key} layer={layer} />)}
                </div>

                <div className="landing-scene__veil" />
            </div>
        </section>
    );
}
